import { Controller } from "@hotwired/stimulus"

// § 3.5 Tier 4 Step 43 — Right-rail "Action Items on This Model"
// panel for the Forge Viewer page. Reads the same `/pins.json`
// endpoint the pin overlay uses so the rail and the in-scene sprites
// stay in lockstep without two round-trips.
//
// Each row links to the action item's slide-over and dispatches the
// `forge:restore-viewpoint` custom event the forge_viewer controller
// listens for, so a click on a row jumps the camera to the captured
// viewpoint just like clicking the pin in the scene.
const STATUS_LABEL = {
  open:        { label: "Open",        cls: "bg-rose-50 text-rose-700"  },
  in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-700" },
  resolved:    { label: "Resolved",    cls: "bg-emerald-50 text-emerald-700" },
  unknown:     { label: "Unknown",     cls: "bg-gray-100 text-gray-600" }
}

export default class extends Controller {
  static targets = ["list", "empty", "count"]
  static values = { pinsUrl: String }

  connect() {
    this._fetch()
  }

  async _fetch() {
    if (!this.hasPinsUrlValue) return
    try {
      const res = await fetch(this.pinsUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return
      const json = await res.json()
      const pins = Array.isArray(json.pins) ? json.pins : []
      this._render(pins)
    } catch (_) {
      // Silent fail — the panel renders the empty state by default.
    }
  }

  _render(pins) {
    if (this.hasCountTarget) {
      this.countTarget.textContent = pins.length === 0 ? "" :
        pins.length === 1 ? "1 item" : `${pins.length} items`
    }
    if (pins.length === 0) {
      this.emptyTarget.classList.remove("hidden")
      this.listTarget.querySelectorAll("[data-pin-row]").forEach(el => el.remove())
      return
    }
    this.emptyTarget.classList.add("hidden")
    const escape = (s) => String(s == null ? "" : s).replace(/[<>"&]/g, (c) => ({
      "<": "&lt;", ">": "&gt;", "\"": "&quot;", "&": "&amp;"
    }[c]))
    const html = pins.map(pin => {
      const ds = STATUS_LABEL[pin.display_status] || STATUS_LABEL.unknown
      const code = pin.code ? ` <span class="text-[11px] text-gray-500">${escape(pin.code)}</span>` : ""
      return `
        <div class="px-3 py-2 hover:bg-gray-50 cursor-pointer"
             data-pin-row="1"
             data-pin-id="${pin.id}"
             data-punchlist-id="${pin.punchlist_id || ""}"
             data-viewpoint-id="${pin.viewpoint_id || ""}"
             data-slide-over-url="${escape(pin.slide_over_url || "")}"
             data-action="click->bim-pins-rail#openPin">
          <div class="flex items-start gap-2 mb-1">
            <span class="mt-1 inline-block w-2 h-2 rounded-full ${this._statusDotClass(pin.display_status)}"></span>
            <div class="min-w-0 flex-1">
              <div class="text-[13px] text-gray-900 truncate">${escape(pin.title || "Untitled")}${code}</div>
              ${pin.kind_name ? `<div class="text-[11px] text-gray-500 truncate">${escape(pin.kind_name)}</div>` : ""}
            </div>
            <span class="text-[10px] uppercase font-medium px-1.5 py-0.5 rounded ${ds.cls} shrink-0">${ds.label}</span>
          </div>
        </div>
      `
    }).join("")
    // Replace just the row nodes (preserve the `<p data-target=empty>`).
    this.listTarget.querySelectorAll("[data-pin-row]").forEach(el => el.remove())
    this.listTarget.insertAdjacentHTML("beforeend", html)
  }

  openPin(event) {
    const row = event.currentTarget
    const slideOverUrl = row.dataset.slideOverUrl
    const viewpointId  = row.dataset.viewpointId
    if (viewpointId) {
      const ev = new CustomEvent("forge:restore-viewpoint", { detail: { viewpointId }, bubbles: true })
      this.element.dispatchEvent(ev)
    }
    if (slideOverUrl) {
      // Same tab — the rail is a navigation surface, not a popup
      // launcher (the pin overlay is the popup launcher per Step 42).
      window.location.href = slideOverUrl
    }
  }

  _statusDotClass(status) {
    switch (status) {
      case "open":        return "bg-rose-500"
      case "in_progress": return "bg-amber-500"
      case "resolved":    return "bg-emerald-500"
      default:            return "bg-gray-400"
    }
  }
}
